import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SqlEditor } from '@/components/ui/SqlEditor';
import { Badge } from '@/components/ui/badge';
import { apiGet } from '@/api';
import {
    Server, ChevronDown, ChevronRight, AlertCircle, CheckCircle2, Clock,
    Database, Lock, Link2, RefreshCw, LayoutGrid, Table2, Unlock, Info
} from 'lucide-react';
import { useTimezoneFormatter } from '@/hooks/useTimezoneFormatter';

// ── Types ──────────────────────────────────────────────────────────────────────
type SanitizationMode = 'MASK' | 'REDACT' | 'HASH' | 'PASSTHROUGH' | 'AUTO';
type ConnectionMode   = 'saved' | 'custom';

interface RelayAgent {
    id: string; name: string; description?: string;
    status: 'ACTIVE' | 'IDLE' | 'PENDING' | 'REVOKED' | string;
    version?: string; heartbeat_at?: string; tenant_id?: string;
}

const DB_TYPES = new Set([0, 1, 2, 3, 4, 5, 6, 7, 8, 13]);
const DB_TYPE_LABELS: Record<number, string> = {
    0: 'PostgreSQL', 1: 'MySQL', 2: 'SQL Server', 3: 'Oracle',
    4: 'Snowflake', 5: 'BigQuery', 6: 'Redshift', 7: 'Azure SQL',
    8: 'Synapse', 13: 'DuckDB',
};

interface DataSourceOption { id: string; name: string; type: number; }
interface DataModelColumn  { name: string; data_type: string; is_pd?: boolean; synonyms?: string; }
interface DataModelTable   { name: string; source_name?: string; columns: DataModelColumn[]; }
interface DataModel        { id: string; name: string; datasource_id: string; tables: DataModelTable[]; }

interface RelaySourceConfigProps {
    node: any;
    onChangeData: (key: string | Record<string, any>, value?: any) => void;
}

const STATUS_STYLES: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: typeof CheckCircle2 }> = {
    ACTIVE:  { variant: 'default',     icon: CheckCircle2 },
    IDLE:    { variant: 'secondary',   icon: Clock },
    PENDING: { variant: 'outline',     icon: Clock },
    REVOKED: { variant: 'destructive', icon: AlertCircle },
};

const SANITIZE_OPTIONS: { value: SanitizationMode; label: string; hint: string }[] = [
    { value: 'AUTO',        label: 'Auto (Agent Default)', hint: 'Use policy set on the agent registration' },
    { value: 'MASK',        label: 'Mask',                 hint: 'Replace with *** or partial mask' },
    { value: 'REDACT',      label: 'Redact',               hint: 'Replace with [REDACTED]' },
    { value: 'HASH',        label: 'Hash (SHA-256)',        hint: 'One-way deterministic hash' },
    { value: 'PASSTHROUGH', label: 'Pass-through',         hint: 'No sanitization applied' },
];

// Build auto SQL from a data model table
const buildSqlFromTable = (table: DataModelTable): string => {
    if (!table?.columns?.length) return `SELECT * FROM ${table?.source_name || table?.name || ''}`;
    const cols = table.columns.map(c => c.name).join(',\n    ');
    return `SELECT\n    ${cols}\nFROM ${table.source_name || table.name}`;
};

// ── Component ─────────────────────────────────────────────────────────────────
export const RelaySourceConfig = ({ node, onChangeData }: RelaySourceConfigProps) => {
    const { formatDate } = useTimezoneFormatter();
    const data = node.data || {};

    const [agents, setAgents]             = useState<RelayAgent[]>([]);
    const [dataSources, setDataSources]   = useState<DataSourceOption[]>([]);
    const [dataModels, setDataModels]     = useState<DataModel[]>([]);
    const [agentLoading, setAgentLoading] = useState(false);
    const [dsLoading, setDsLoading]       = useState(false);
    const [dmLoading, setDmLoading]       = useState(false);
    const [agentError, setAgentError]     = useState<string | null>(null);
    const [dsError, setDsError]           = useState<string | null>(null);
    const [showSanitize, setShowSanitize] = useState(false);

    // The selected data model's full object (for table/column preview)
    const selectedDataModel = dataModels.find(m => m.id === data.dataModelId) || null;
    // Current table being previewed
    const selectedTableName: string = data.selectedTableName || selectedDataModel?.tables?.[0]?.name || '';
    const selectedTable = selectedDataModel?.tables?.find(t => t.name === selectedTableName) || selectedDataModel?.tables?.[0] || null;

    const connectionMode: ConnectionMode = data.connectionMode || 'saved';
    const outputColumns: string[]        = data.outputColumns  || [];
    const isDataModelMode = !!data.dataModelId;

    // ── Fetch relay agents ────────────────────────────────────────────────────
    useEffect(() => {
        setAgentLoading(true);
        apiGet('/relay/agents')
            .then((res: any) => Array.isArray(res) && setAgents(res.filter((a: RelayAgent) => a.status !== 'REVOKED')))
            .catch(() => setAgentError('Could not load relay agents.'))
            .finally(() => setAgentLoading(false));
    }, []);

    // ── Fetch DataSources (DB types only) ─────────────────────────────────────
    const fetchDataSources = () => {
        setDsLoading(true); setDsError(null);
        apiGet('/datasources')
            .then((res: any) => Array.isArray(res) && setDataSources(
                res.filter((ds: any) => DB_TYPES.has(ds.type))
                   .map((ds: any) => ({ id: ds.id, name: ds.name, type: ds.type }))
            ))
            .catch(() => setDsError('Could not load saved connections.'))
            .finally(() => setDsLoading(false));
    };

    // ── Fetch Data Models ─────────────────────────────────────────────────────
    const fetchDataModels = () => {
        setDmLoading(true);
        apiGet('/datamodels')
            .then((res: any) => Array.isArray(res) && setDataModels(res))
            .catch(() => {})
            .finally(() => setDmLoading(false));
    };

    useEffect(() => {
        if (connectionMode === 'saved') fetchDataSources();
        fetchDataModels();
    }, [connectionMode]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const selectedAgent  = agents.find(a => a.id === data.agentId);
    const selectedSource = dataSources.find(ds => ds.id === data.dataSourceId);

    const handleAgentChange = (agentId: string) => {
        const agent = agents.find(a => a.id === agentId);
        onChangeData({ agentId, agentName: agent?.name || '' });
    };

    const handleModeChange = (mode: ConnectionMode) => {
        onChangeData({ connectionMode: mode, dataSourceId: undefined, dataSourceName: undefined,
                       dataSourceType: undefined, customConnectionUrl: undefined });
        if (mode === 'saved') fetchDataSources();
    };

    const handleDataSourceChange = (dsId: string) => {
        const ds = dataSources.find(d => d.id === dsId);
        onChangeData({ dataSourceId: dsId, dataSourceName: ds?.name || '', dataSourceType: ds?.type, connectionMode: 'saved' });
    };

    const handleDataModelChange = (modelId: string) => {
        if (!modelId || modelId === '__none__') {
            // Clear data model — switch to manual SQL
            onChangeData({ dataModelId: undefined, selectedTableName: undefined, pdColumns: [], sql: '' });
            return;
        }
        const model = dataModels.find(m => m.id === modelId);
        const firstTable = model?.tables?.[0] || null;
        const phiCols = firstTable?.columns?.filter(c => c.is_pd).map(c => c.name) || [];
        const autoSql = firstTable ? buildSqlFromTable(firstTable) : '';
        onChangeData({
            dataModelId:       modelId,
            dataModelName:     model?.name || '',
            selectedTableName: firstTable?.name || '',
            pdColumns:        phiCols,
            sql:               autoSql,
        });
    };

    const handleTableChange = (tableName: string) => {
        const table = selectedDataModel?.tables?.find(t => t.name === tableName);
        const phiCols = table?.columns?.filter(c => c.is_pd).map(c => c.name) || [];
        const autoSql = table ? buildSqlFromTable(table) : '';
        onChangeData({ selectedTableName: tableName, pdColumns: phiCols, sql: autoSql });
    };

    const handleSanitizationChange = (column: string, mode: SanitizationMode) => {
        const current = { ...(data.sanitization || {}) };
        if (mode === 'AUTO') { delete current[column]; } else { current[column] = mode; }
        onChangeData('sanitization', current);
    };

    const globalSanitization: SanitizationMode = data.globalSanitization || 'AUTO';
    const pdColumns: string[] = data.pdColumns || [];

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="space-y-6">

            {/* ── 1. Agent Selector ────────────────────────────────────────── */}
            <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                    <Server className="h-3.5 w-3.5 text-blue-500" /> Relay Agent
                </label>
                {agentError && (
                    <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />{agentError}
                    </div>
                )}
                <Select value={data.agentId || ''} onValueChange={handleAgentChange} disabled={agentLoading || agents.length === 0}>
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder={agentLoading ? 'Loading agents…' : agents.length === 0 ? 'No active agents found' : 'Select a Relay Agent…'} />
                    </SelectTrigger>
                    <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                        {agents.map(agent => {
                            const style = STATUS_STYLES[agent.status] || STATUS_STYLES['IDLE'];
                            const Icon  = style.icon;
                            return (
                                <SelectItem key={agent.id} value={agent.id}>
                                    <div className="flex items-center gap-2">
                                        <Icon className="h-3 w-3 text-gray-400 shrink-0" />
                                        <span>{agent.name}</span>
                                        <Badge variant={style.variant} className="text-[9px] px-1 py-0 ml-auto">{agent.status}</Badge>
                                    </div>
                                </SelectItem>
                            );
                        })}
                    </SelectContent>
                </Select>
                {selectedAgent && (
                    <div className="p-2 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-between">
                        <span className="text-[11px] font-medium text-blue-900">{selectedAgent.name}</span>
                        <Badge variant={STATUS_STYLES[selectedAgent.status]?.variant || 'secondary'} className="text-[9px]">
                            {selectedAgent.status}
                        </Badge>
                    </div>
                )}
            </div>

            {/* ── 2. Database Connection ───────────────────────────────────── */}
            <div className="space-y-3">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                    <Database className="h-3.5 w-3.5 text-green-500" /> Database Connection
                </label>
                <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs font-medium">
                    <button type="button" onClick={() => handleModeChange('saved')}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 transition-colors ${connectionMode === 'saved' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                        <Database className="h-3 w-3" /> Saved Connection
                    </button>
                    <button type="button" onClick={() => handleModeChange('custom')}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 border-l border-gray-200 transition-colors ${connectionMode === 'custom' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                        <Link2 className="h-3 w-3" /> Custom URL
                    </button>
                </div>

                {connectionMode === 'saved' && (
                    <div className="space-y-2">
                        {dsError && <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700"><AlertCircle className="h-3.5 w-3.5 shrink-0" />{dsError}</div>}
                        <div className="flex gap-2">
                            <Select value={data.dataSourceId || ''} onValueChange={handleDataSourceChange} disabled={dsLoading}>
                                <SelectTrigger className="w-full text-sm">
                                    <SelectValue placeholder={dsLoading ? 'Loading…' : dataSources.length === 0 ? 'No DB sources found' : 'Select a saved connection…'} />
                                </SelectTrigger>
                                <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                                    {dataSources.map(ds => (
                                        <SelectItem key={ds.id} value={ds.id}>
                                            <div className="flex items-center gap-2 w-full">
                                                <Database className="h-3 w-3 text-gray-400 shrink-0" />
                                                <span className="flex-1">{ds.name}</span>
                                                <span className="text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{DB_TYPE_LABELS[ds.type] || `Type ${ds.type}`}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <button type="button" onClick={fetchDataSources} disabled={dsLoading}
                                className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 transition-colors shrink-0" title="Refresh">
                                <RefreshCw className={`h-3.5 w-3.5 ${dsLoading ? 'animate-spin' : ''}`} />
                            </button>
                        </div>
                        {selectedAgent.description && (
                            <p className="text-[10px] text-blue-700">{selectedAgent.description}</p>
                        )}
                        <div className="flex gap-3 text-[10px] text-blue-500">
                            {selectedAgent.version && <span>v{selectedAgent.version}</span>}
                            {selectedAgent.heartbeat_at && (
                                <span>Last seen: {formatDate(selectedAgent.heartbeat_at)}</span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* ── 3. Data Model (Optional) — PD Column Preview ───────────── */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                        <LayoutGrid className="h-3.5 w-3.5 text-purple-500" /> Data Model
                        <span className="text-[9px] font-normal text-gray-400 normal-case tracking-normal bg-gray-100 px-1.5 py-0.5 rounded">Optional</span>
                    </label>
                    {isDataModelMode && (
                        <button type="button" onClick={() => handleDataModelChange('__none__')}
                            className="text-[10px] text-red-500 hover:text-red-700 hover:underline transition-colors">
                            Clear
                        </button>
                    )}
                </div>

                <Select value={data.dataModelId || '__none__'} onValueChange={handleDataModelChange} disabled={dmLoading}>
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder={dmLoading ? 'Loading data models…' : 'Select a data model (auto-generates SQL)…'} />
                    </SelectTrigger>
                    <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                        <SelectItem value="__none__">
                            <span className="text-gray-400 italic text-xs">None — write SQL manually</span>
                        </SelectItem>
                        {dataModels.map(dm => (
                            <SelectItem key={dm.id} value={dm.id}>
                                <div className="flex items-center gap-2">
                                    <LayoutGrid className="h-3 w-3 text-purple-400 shrink-0" />
                                    <span>{dm.name}</span>
                                    <span className="text-[9px] text-gray-400 ml-auto">{dm.tables?.length || 0} tables</span>
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                {/* Table & Column Preview */}
                {isDataModelMode && selectedDataModel && (
                    <div className="border border-purple-100 rounded-xl overflow-hidden bg-white">

                        {/* Table selector header */}
                        <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border-b border-purple-100">
                            <Table2 className="h-3.5 w-3.5 text-purple-500 shrink-0" />
                            <span className="text-[11px] font-semibold text-purple-800 flex-1">Table</span>
                            <Select value={selectedTableName} onValueChange={handleTableChange}>
                                <SelectTrigger className="h-6 text-[11px] px-2 w-auto min-w-[130px] bg-white border-purple-200">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                                    {selectedDataModel.tables.map(t => (
                                        <SelectItem key={t.name} value={t.name} className="text-xs">{t.source_name || t.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Column list with PD status */}
                        {selectedTable && (
                            <div>
                                {/* Column list header */}
                                <div className="grid grid-cols-[1fr_auto_auto] px-3 py-1.5 bg-gray-50 border-b border-gray-100 text-[10px] font-semibold text-gray-500 uppercase tracking-wider gap-2">
                                    <span>Column</span>
                                    <span>Type</span>
                                    <span className="text-center w-12">PD</span>
                                </div>

                                {/* Scrollable column rows */}
                                <div className="max-h-[200px] overflow-y-auto divide-y divide-gray-50">
                                    {selectedTable.columns.map(col => (
                                        <div key={col.name}
                                            className={`grid grid-cols-[1fr_auto_auto] items-center px-3 py-1.5 gap-2 transition-colors ${col.is_pd ? 'bg-red-50' : 'bg-white hover:bg-gray-50'}`}>
                                            <span className={`text-[11px] font-mono truncate ${col.is_pd ? 'text-red-700 font-semibold' : 'text-slate-700'}`}>
                                                {col.name}
                                            </span>
                                            <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 rounded shrink-0">{col.data_type}</span>
                                            {/* PD badge — read-only, shows what is set in Data Model */}
                                            <div className="w-12 flex justify-center">
                                                {col.is_pd ? (
                                                    <span className="flex items-center gap-0.5 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                                                        <Lock size={8} /> PD
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center gap-0.5 text-green-600 text-[9px] font-medium">
                                                        <Unlock size={8} /> OK
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* PD summary footer */}
                                <div className="px-3 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                                    <span className="text-[10px] text-gray-500">
                                        {selectedTable.columns.length} columns
                                    </span>
                                    {pdColumns.length > 0 ? (
                                        <span className="text-[10px] text-red-600 font-semibold flex items-center gap-1">
                                            <Lock size={10} /> {pdColumns.length} column{pdColumns.length > 1 ? 's' : ''} will be sanitized
                                        </span>
                                    ) : (
                                        <span className="text-[10px] text-green-600 flex items-center gap-1">
                                            <Unlock size={10} /> No PD columns marked
                                        </span>
                                    )}
                                </div>

                                {/* Tip */}
                                <div className="mx-2 mb-2 mt-1 p-2 bg-blue-50 border border-blue-100 rounded-lg flex items-start gap-1.5">
                                    <Info className="h-3 w-3 text-blue-500 shrink-0 mt-0.5" />
                                    <p className="text-[10px] text-blue-700 leading-relaxed">
                                        PD columns are set in the <strong>Data Model Editor</strong> (🔒 lock toggle).
                                        SQL is auto-generated — you can also edit it manually below.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── 4. SQL Query ─────────────────────────────────────────────── */}
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                        SQL Query
                        {isDataModelMode && (
                            <span className="text-[9px] font-normal tracking-normal bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded normal-case">
                                Auto-generated
                            </span>
                        )}
                    </label>
                    {isDataModelMode && (
                        <span className="text-[10px] text-gray-400 italic">Edit to customise</span>
                    )}
                </div>

                <div className={`rounded-lg overflow-hidden transition-all ${isDataModelMode ? 'ring-1 ring-purple-200' : ''}`}>
                    <SqlEditor
                        value={data.sql || ''}
                        onChange={(val: string) => onChangeData('sql', val)}
                        className={isDataModelMode ? 'border-purple-200 bg-purple-50/30' : 'border-gray-200'}
                    />
                </div>

                <p className="text-[10px] text-gray-400">
                    {isDataModelMode
                        ? 'SQL is generated from the selected Data Model. PD columns will be automatically sanitized before upload.'
                        : 'Executed on-premises by the relay agent. PD columns are sanitized before upload.'}
                </p>
            </div>

            {/* ── 5. PD Sanitization (collapsible) ───────────────────────── */}
            <div className="border border-gray-100 rounded-lg overflow-hidden">
                <button type="button"
                    className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                    onClick={() => setShowSanitize(v => !v)}>
                    <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">PD Sanitization</span>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-400">
                            {Object.keys(data.sanitization || {}).length > 0
                                ? `${Object.keys(data.sanitization).length} overrides`
                                : 'Agent defaults'}
                        </span>
                        {showSanitize ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
                    </div>
                </button>

                {showSanitize && (
                    <div className="px-3 pb-3 pt-2 space-y-3">
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-medium text-gray-500">Global Default Strategy</label>
                            <Select value={globalSanitization} onValueChange={(v) => onChangeData('globalSanitization', v)}>
                                <SelectTrigger className="w-full text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                                    {SANITIZE_OPTIONS.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                            <div className="text-xs font-medium">{opt.label}</div>
                                            <div className="text-[10px] text-gray-400">{opt.hint}</div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {outputColumns.length > 0 && (
                            <div className="border border-gray-100 rounded-lg overflow-hidden">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-100">
                                            <th className="text-left px-2 py-1.5 font-medium text-gray-500">Column</th>
                                            <th className="text-left px-2 py-1.5 font-medium text-gray-500">Strategy</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {outputColumns.map(col => (
                                            <tr key={col} className="border-b border-gray-50 last:border-0">
                                                <td className="px-2 py-1 text-gray-700 font-mono">{col}</td>
                                                <td className="px-2 py-1">
                                                    <Select value={(data.sanitization || {})[col] || 'AUTO'}
                                                        onValueChange={(v) => handleSanitizationChange(col, v as SanitizationMode)}>
                                                        <SelectTrigger className="h-6 text-[10px] px-2 py-0"><SelectValue /></SelectTrigger>
                                                        <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                                                            {SANITIZE_OPTIONS.map(opt => (
                                                                <SelectItem key={opt.value} value={opt.value} className="text-[10px]">{opt.label}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {outputColumns.length === 0 && (
                            <p className="text-[10px] text-gray-400 italic">Run the flow once to detect output columns for per-column overrides.</p>
                        )}
                        <div className="p-2 bg-amber-50 border border-amber-100 rounded text-[10px] text-amber-800">
                            <strong>Note:</strong> Sanitization runs on-premises before data leaves your network.
                        </div>
                    </div>
                )}
            </div>

            {/* ── 6. Ready summary ─────────────────────────────────────────── */}
            {data.agentId && (data.dataSourceId || data.customConnectionUrl) && data.sql && (
                <div className="p-2.5 bg-green-50 border border-green-100 rounded-lg space-y-1">
                    <p className="text-[10px] font-semibold text-green-700 uppercase tracking-wider">✅ Ready to Run</p>
                    <div className="text-[10px] text-gray-600 space-y-0.5">
                        <div>Agent: <span className="font-medium text-gray-900">{data.agentName || data.agentId}</span></div>
                        <div>Connection: {connectionMode === 'saved'
                            ? <span className="font-medium text-gray-900">{data.dataSourceName} <span className="text-green-600">🔒 secure</span></span>
                            : <span className="text-amber-700 font-medium">Custom URL ⚠️</span>}
                        </div>
                        {isDataModelMode && <div>Data Model: <span className="font-medium text-gray-900">{data.dataModelName}</span></div>}
                        {pdColumns.length > 0 && (
                            <div className="flex items-center gap-1 text-red-600">
                                <Lock size={10} /> {pdColumns.length} PD column{pdColumns.length > 1 ? 's' : ''} will be sanitized: <span className="font-mono">{pdColumns.join(', ')}</span>
                            </div>
                        )}
                        <div>Query: <span className="font-mono text-gray-900">{(data.sql || '').slice(0, 60)}{(data.sql || '').length > 60 ? '…' : ''}</span></div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RelaySourceConfig;
